import { RefObject, useEffect, useState } from 'react'

import { getToken } from '@helpscout/hsds-tokens/getToken'
import throttle from 'lodash.throttle'

const BOX_SHADOW_INITIAL = ''
const BOX_SHADOW_SCROLLED = getToken('shadow.50')
const BOX_SHADOW_BOTTOM_SCROLLED = getToken('shadow.inverted.50')

export function useScrollShadow({
  bottomRef,
  drawInitialShadowsDelay = 0,
  scrollableRef,
  shadows = {},
  topRef,
  enabled = true,
}: {
  enabled?: boolean
  bottomRef: RefObject<HTMLElement>
  drawInitialShadowsDelay?: number
  scrollableRef: RefObject<HTMLElement>
  shadows?: { initial?: string; scrolled?: string; bottomScrolled?: string }
  topRef: RefObject<HTMLElement>
}) {
  const initialShadow = shadows.initial || BOX_SHADOW_INITIAL
  const scrolledShadow = shadows.scrolled || BOX_SHADOW_SCROLLED
  const bottomScrolledShadow =
    shadows.bottomScrolled || BOX_SHADOW_BOTTOM_SCROLLED

  const [isTopScrolled, setIsTopScrolled] = useState<boolean | null>(null)
  const [isBottomScrolled, setIsBottomScrolled] = useState<boolean | null>(null)
  const topElement = getElement(topRef)
  const bottomElement = getElement(bottomRef)
  const scrollableElement = getElement(scrollableRef)
  const scrollableHeight =
    scrollableElement && scrollableElement.getBoundingClientRect().height

  useEffect(() => {
    if (!enabled) return

    const timeoutID = setTimeout(() => {
      if (scrollableElement) {
        const { isBottomScrolled, isTopScrolled } =
          getScrollableSectionsState(scrollableElement)
        applyShadows(topElement, isTopScrolled, {
          initialShadow,
          scrolledShadow,
        })

        applyShadows(bottomElement, isBottomScrolled, {
          initialShadow,
          scrolledShadow: bottomScrolledShadow,
        })
        setIsTopScrolled(isTopScrolled)
        setIsBottomScrolled(isBottomScrolled)
      }
    }, drawInitialShadowsDelay)

    return () => {
      clearTimeout(timeoutID)
    }
  }, [
    drawInitialShadowsDelay,
    initialShadow,
    scrolledShadow,
    bottomScrolledShadow,
    topElement,
    bottomElement,
    scrollableElement,
    scrollableHeight,
    enabled,
  ])

  const handleOnScroll = throttle(() => {
    if (!enabled) return

    const scrollableElement = getElement(scrollableRef)
    const { isTopScrolled, isBottomScrolled } =
      getScrollableSectionsState(scrollableElement)

    applyShadows(topElement, isTopScrolled, {
      initialShadow,
      scrolledShadow,
    })
    applyShadows(bottomElement, isBottomScrolled, {
      initialShadow,
      scrolledShadow: bottomScrolledShadow,
    })
    setIsTopScrolled(isTopScrolled)
    setIsBottomScrolled(isBottomScrolled)
  }, 100)

  return [handleOnScroll, isTopScrolled, isBottomScrolled, enabled]
}

function getElement(someRef: HTMLElement | RefObject<HTMLElement>) {
  if (someRef instanceof HTMLElement) return someRef
  return someRef && someRef.current
}

function applyShadows(
  element: HTMLElement | null,
  isScrolled: boolean,
  {
    initialShadow,
    scrolledShadow,
  }: { initialShadow: string; scrolledShadow: string }
) {
  if (element) {
    element.style.boxShadow = 'var(--scroll-shadow)'

    if (isScrolled) {
      element.style.setProperty('--scroll-shadow', scrolledShadow)
    } else {
      element.style.setProperty('--scroll-shadow', initialShadow)
    }
  }
}

export function getScrollableSectionsState(scrollable: HTMLElement | null) {
  if (!scrollable) {
    return {
      isTopScrolled: false,
      isBottomScrolled: false,
    }
  }

  const style = window.getComputedStyle(scrollable)
  const paddingTop = Number.parseInt(style.paddingTop, 10)
  const paddingBottom = Number.parseInt(style.paddingBottom, 10)
  const scrollableScrollHeight = scrollable.scrollHeight
  const scrollableHeight = scrollable.offsetHeight
  const scrollablePaddingTop = paddingTop > 0 ? paddingTop - paddingTop / 2 : 0
  const scrollablePaddingBottom =
    paddingBottom > 0 ? paddingBottom + paddingBottom / 2 : 0

  return {
    isTopScrolled: scrollable.scrollTop - scrollablePaddingTop > 0,
    isBottomScrolled:
      scrollable.scrollTop + scrollableHeight + scrollablePaddingBottom <
      scrollableScrollHeight,
  }
}
